import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Lock,
  CheckCircle,
  Engineering,
  Gavel,
  Message,
  WorkOutline,
  VerifiedUser,
  AssignmentTurnedIn
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { status } from '../theme/colors';

/**
 * Dialog shown when a professional tries to perform a gated action
 * without a complete profile. Works for all professional roles.
 */
const ProfileGateDialog = ({ open, onClose, action, profileData }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'contractor';
  const contractorMissingPortfolio = role === 'contractor'
    && profileData?.portfolioRequired
    && !profileData?.hasVerifiablePortfolio
    && profileData?.baseProfileComplete !== false;

  // Role-specific configuration
  const roleConfig = {
    contractor: {
      label: 'Contractor',
      onboardingPath: '/contractor/onboarding',
      icon: <Engineering sx={{ fontSize: 48 }} />,
      setupItems: [
        { primary: 'Your trade & experience', secondary: 'Tell us what kind of work you specialize in' },
        { primary: 'Your location', secondary: 'So we can match you with nearby projects' },
        { primary: 'Professional bio', secondary: 'Our AI writes it for you based on your answers' },
        { primary: 'One verifiable portfolio project', secondary: 'Add completed work with project details and a finished photo' }
      ]
    },
    verifier: {
      label: 'Verifier',
      onboardingPath: '/verifier/onboarding',
      icon: <VerifiedUser sx={{ fontSize: 48 }} />,
      setupItems: [
        { primary: 'Your background & credentials', secondary: 'Show your qualifications and expertise' },
        { primary: 'Your service area', secondary: 'So we can match you with verification requests' },
        { primary: 'Professional bio', secondary: 'Our AI writes it for you, emphasizing independence' }
      ]
    },
    quality_inspector: {
      label: 'Quality Inspector',
      onboardingPath: '/inspector/onboarding',
      icon: <AssignmentTurnedIn sx={{ fontSize: 48 }} />,
      setupItems: [
        { primary: 'Your qualifications & standards', secondary: 'Show your certifications and expertise' },
        { primary: 'Your service area', secondary: 'So we can match you with inspection assignments' },
        { primary: 'Professional bio', secondary: 'Our AI writes it for you, emphasizing technical authority' }
      ]
    },
    project_manager: {
      label: 'Project Manager',
      onboardingPath: '/project-manager/onboarding',
      icon: <WorkOutline sx={{ fontSize: 48 }} />,
      setupItems: [
        { primary: 'Your experience & projects', secondary: 'Tell us about your management background' },
        { primary: 'Your location', secondary: 'So we can match you with project opportunities' },
        { primary: 'Professional bio', secondary: 'Our AI writes it for you based on your answers' }
      ]
    },
    merchant: {
      label: 'Merchant',
      onboardingPath: '/merchant/onboarding',
      icon: <Engineering sx={{ fontSize: 48 }} />,
      setupItems: [
        { primary: 'Material categories you supply', secondary: 'So buyers can find you when their BOQ goes out for quotes' },
        { primary: 'Your service areas & delivery', secondary: 'Where you deliver and how' },
        { primary: 'Business details & registration doc', secondary: 'Bank/mobile-money details and your registration certificate' }
      ]
    }
  };

  const currentRoleConfig = roleConfig[role] || roleConfig.contractor;

  // Action-specific messaging (role-aware)
  const actionMessages = {
    bid: {
      title: `Complete Your Profile to Submit Bids`,
      description: 'Clients want to know who they\'re working with. Set up your profile so they can trust your expertise.',
      icon: <Gavel sx={{ fontSize: 48, color: '#10b981' }} />
    },
    apply: {
      title: `Complete Your Profile to Apply`,
      description: 'Clients need to see your qualifications before you can apply for opportunities. Complete your profile to get started.',
      icon: <WorkOutline sx={{ fontSize: 48, color: '#10b981' }} />
    },
    message: {
      title: 'Complete Your Profile to Send Messages',
      description: 'A complete profile helps build trust. Finish your setup to start communicating.',
      icon: <Message sx={{ fontSize: 48, color: status.info }} />
    },
    accept: {
      title: `Complete Your Profile to Accept ${role === 'verifier' ? 'Verifications' : role === 'quality_inspector' ? 'Inspections' : 'Projects'}`,
      description: 'Before you can accept work, clients need to see your qualifications and experience.',
      icon: <WorkOutline sx={{ fontSize: 48, color: '#f59e0b' }} />
    },
    default: {
      title: 'Complete Your Profile First',
      description: `This action requires a complete ${currentRoleConfig.label.toLowerCase()} profile. It only takes 5 minutes!`,
      icon: <Lock sx={{ fontSize: 48, color: '#999' }} />
    }
  };

  const config = contractorMissingPortfolio
    ? {
        title: 'Add a Verifiable Portfolio to Submit Quotations',
        description: 'Before you bid or quote, clients need to see at least one completed project with real details and a finished photo.',
        icon: <Gavel sx={{ fontSize: 48, color: '#10b981' }} />
      }
    : (actionMessages[action] || actionMessages.default);

  const handleGoToOnboarding = () => {
    onClose();
    navigate(contractorMissingPortfolio ? '/contractor/portfolio' : currentRoleConfig.onboardingPath);
  };

  const handleBrowseInstead = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' }
      }}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          p: 3,
          textAlign: 'center'
        }}
      >
        {config.icon && React.cloneElement(config.icon, { sx: { ...config.icon.props.sx, color: 'white' } })}
        <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
          {config.title}
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          {config.description}
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          What you'll set up:
        </Typography>
        <List dense>
          {currentRoleConfig.setupItems.map((item, index) => (
            <ListItem key={index}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={item.primary}
                secondary={item.secondary}
              />
            </ListItem>
          ))}
        </List>

        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: '#f0fdf4',
            borderRadius: 2,
            border: '1px solid #bbf7d0',
            textAlign: 'center'
          }}
        >
          {React.cloneElement(currentRoleConfig.icon, { sx: { color: '#10b981', fontSize: 28 } })}
          <Typography variant="body2" sx={{ mt: 0.5, color: '#166534' }}>
            {contractorMissingPortfolio
              ? 'Add one real completed project so clients can verify your workmanship'
              : 'Takes about 5 minutes — our AI assistant guides you through it'}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, flexDirection: 'column', gap: 1 }}>
        <Button
          variant="contained"
          onClick={handleGoToOnboarding}
          size="large"
          fullWidth
          sx={{
            bgcolor: '#10b981',
            '&:hover': { bgcolor: '#059669' },
            py: 1.5,
            fontWeight: 600,
            fontSize: '1rem'
          }}
        >
          {contractorMissingPortfolio ? 'Add Portfolio Project' : 'Complete My Profile'}
        </Button>
        <Button
          onClick={handleBrowseInstead}
          color="inherit"
          fullWidth
          sx={{ color: '#666', fontSize: '0.85rem' }}
        >
          I'll do this later — keep browsing
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileGateDialog;
